import React, { useMemo, useState, useEffect } from 'react';
import HeroBgAnimation from '../HeroBgAnimation';
import './HeroSection.css';
import { useBio } from '../../contexts/BioContext';
import { useTranslations } from '../../contexts/TranslationsContext';
import { getLanguage } from '../../firebase';

import {
  HeroContainer,
  HeroBg,
  HeroLeftContainer,
  HeroRightContainer,
  HeroInnerContainer,
  TextLoop,
  Title,
  Span,
  SubTitle,
  LetsCollaborateButton,
} from './HeroSectionStyles';

const HeroSection = React.memo(() => {
  const { bio } = useBio();
  const { translations } = useTranslations();
  const [language, setLanguage] = useState(getLanguage());
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);

  const bioData = useMemo(() => {
    return (
      bio || {
        name: '',
        roles: [],
        description: '',
        heroImgURL: '/images/hero/HeroImage.webp',
      }
    );
  }, [bio]);

  useEffect(() => {
    const handleLanguageChange = () => {
      const newLang = getLanguage();
      setLanguage(newLang);
      setCurrentRoleIndex(0);
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () =>
      window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    if (!bioData?.roles?.length) return;

    const interval = setInterval(() => {
      setCurrentRoleIndex((prev) => (prev + 1) % bioData.roles.length);
    }, 7000);

    return () => clearInterval(interval);
  }, [bioData.roles]);

  return (
    <div id="about">
      <HeroContainer>
        <HeroBg>
          <HeroBgAnimation />
        </HeroBg>
        <HeroInnerContainer>
          <HeroLeftContainer id="Left">
            <Title>{bioData.name}</Title>
            <TextLoop>
              <Span dir={language === 'ar' ? 'rtl' : 'ltr'}>
                <div key={currentRoleIndex} className="role-text">
                  {bioData.roles[currentRoleIndex]}
                </div>
              </Span>
            </TextLoop>
            <SubTitle>{bioData.description}</SubTitle>
            <LetsCollaborateButton
              as="a"
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                const target = document.getElementById('contact');
                if (target) target.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {translations?.hero?.letsCollaborate || "Let's Collaborate"}
            </LetsCollaborateButton>
          </HeroLeftContainer>
          <HeroRightContainer id="Right">
            <img
              src={bioData.heroImgURL}
              alt={`${bioData.name}`}
              loading="eager"
              fetchpriority="high"
              width="220"
              height="220"
              decoding="sync"
              crossOrigin="anonymous"
              className="hero-image"
            />
          </HeroRightContainer>
        </HeroInnerContainer>
      </HeroContainer>
    </div>
  );
});

HeroSection.displayName = 'HeroSection';

export default HeroSection;
